/**
 * Utility to generate Arabic property titles when titleAr is missing
 */

// Property type translations
const PROPERTY_TYPE_AR: Record<string, string> = {
  apartment: 'شقة',
  villa: 'فيلا',
  floor: 'دور',
  land: 'أرض',
  residential_land: 'أرض سكنية',
  commercial_land: 'أرض تجارية',
  office: 'مكتب',
  shop: 'محل',
  showroom: 'معرض',
  building: 'عمارة',
  residential_building: 'عمارة سكنية',
  commercial_building: 'عمارة تجارية',
  warehouse: 'مستودع',
  farm: 'مزرعة',
  chalet: 'شاليه',
  rest_house: 'استراحة',
  townhouse: 'تاون هاوس',
  studio: 'استوديو',
  penthouse: 'بنتهاوس',
  duplex: 'دوبلكس',
  room: 'غرفة',
  hotel: 'فندق',
  factory: 'مصنع',
  mall: 'مول',
};

// Listing type translations
const LISTING_TYPE_AR: Record<string, string> = {
  sale: 'للبيع',
  rent: 'للإيجار',
  short_rent: 'للإيجار قصير المدى',
};

// City name translations (common Saudi cities)
const CITY_AR: Record<string, string> = {
  riyadh: 'الرياض',
  jeddah: 'جدة',
  makkah: 'مكة المكرمة',
  mecca: 'مكة المكرمة',
  madina: 'المدينة المنورة',
  medina: 'المدينة المنورة',
  madinah: 'المدينة المنورة',
  dammam: 'الدمام',
  'al khobar': 'الخبر',
  khobar: 'الخبر',
  dhahran: 'الظهران',
  tabuk: 'تبوك',
  abha: 'أبها',
  taif: 'الطائف',
  jubail: 'الجبيل',
  'al jubail': 'الجبيل',
  yanbu: 'ينبع',
  'khamis mushait': 'خميس مشيط',
  khamis: 'خميس مشيط',
  najran: 'نجران',
  jazan: 'جازان',
  jizan: 'جازان',
  hail: 'حائل',
  'al ahsa': 'الأحساء',
  hofuf: 'الأحساء',
  buraidah: 'بريدة',
  buraydah: 'بريدة',
  sakakah: 'سكاكا',
  arar: 'عرعر',
  qatif: 'القطيف',
  'al qatif': 'القطيف',
};

/**
 * Check if a string contains Arabic characters
 */
function containsArabic(text: string): boolean {
  return /[\u0600-\u06FF]/.test(text);
}

/**
 * Get Arabic property type name
 */
function getPropertyTypeAr(type: string): string {
  const normalized = type.toLowerCase().replace(/[-\s]/g, '_');
  return PROPERTY_TYPE_AR[normalized] || 'عقار';
}

/**
 * Get Arabic listing type
 */
function getListingTypeAr(type: string): string {
  const normalized = type.toLowerCase().replace(/[-\s]/g, '_');
  return LISTING_TYPE_AR[normalized] || 'للبيع';
}

/**
 * Get Arabic city name
 */
function getCityAr(city: string): string {
  const normalized = city.toLowerCase().trim();
  return CITY_AR[normalized] || city;
}

/**
 * Generate an Arabic title for a property
 */
export function generateArabicTitle(property: {
  title?: string;
  titleAr?: string;
  propertyType?: string;
  listingType?: string;
  city?: string;
  cityAr?: string;
  bedrooms?: number;
}): string {
  // If Arabic title exists, use it
  if (property.titleAr) {
    return property.titleAr;
  }

  // If English title contains Arabic, use it as Arabic title
  if (property.title && containsArabic(property.title)) {
    return property.title;
  }

  // Generate Arabic title from property data
  const typeAr = getPropertyTypeAr(property.propertyType || 'apartment');
  const listingAr = getListingTypeAr(property.listingType || 'sale');
  const cityAr = property.cityAr || getCityAr(property.city || '');

  // Build title: "شقة للبيع في الرياض" or "فيلا 4 غرف للبيع في جدة"
  let title = typeAr;

  // Add bedrooms if applicable
  if (property.bedrooms && property.bedrooms > 0 &&
      ['apartment', 'villa', 'floor', 'duplex', 'townhouse', 'penthouse'].includes(
        (property.propertyType || '').toLowerCase()
      )) {
    title += ` ${property.bedrooms} غرف`;
  }

  title += ` ${listingAr}`;

  if (cityAr) {
    title += ` في ${cityAr}`;
  }

  return title;
}

/**
 * Get display title based on language
 */
export function getPropertyDisplayTitle(
  property: {
    title?: string;
    titleAr?: string;
    propertyType?: string;
    listingType?: string;
    city?: string;
    cityAr?: string;
    bedrooms?: number;
  },
  isRTL: boolean
): string {
  if (isRTL) {
    return generateArabicTitle(property);
  }
  return property.title || 'Property';
}

export default getPropertyDisplayTitle;
